import apiClient from '../api/apiClient';
import axios from 'axios';
import { getRecommendationConnection } from './meetingRecommendationConfig';

// Temporary direct access is explicitly opt-in. A backend path always takes priority.
const connection = getRecommendationConnection({
  proxyPath: process.env.REACT_APP_MEETING_RECOMMENDATIONS_PATH,
  directEnabled: process.env.REACT_APP_SNAPSOFT_DIRECT_ENABLED,
  endpoint: process.env.REACT_APP_SNAPSOFT_ENDPOINT,
  apiKey: process.env.REACT_APP_SNAPSOFT_API_KEY,
});
export const recommendationPath = connection?.url || '';
// Separate client prevents Social Paws headers/credentials leaking to SnapSoft.
const snapSoftClient = axios.create({ withCredentials: false });

export function coordinates(value) {
  const lat = value?.lat ?? value?.latitude ?? value?.user_latitude;
  const lng = value?.lng ?? value?.longitude ?? value?.user_longitude;
  if (lat == null || lng == null || String(lat).trim() === '' || String(lng).trim() === '') return null;
  const point = { lat: Number(lat), lng: Number(lng) };
  return Number.isFinite(point.lat) && Number.isFinite(point.lng) &&
    Math.abs(point.lat) <= 90 && Math.abs(point.lng) <= 180 &&
    !(point.lat === 0 && point.lng === 0) ? point : null;
}

export function resolveRoles(current, partner, dogId) {
  const currentId = Number(current?.id);
  const partnerId = Number(partner?.id);
  if (!Number.isSafeInteger(currentId) || currentId <= 0 || !Number.isSafeInteger(partnerId) || partnerId <= 0 || currentId === partnerId) return null;
  const ownsDog = (person) => dogId && person?.dogs?.some((dog) => String(dog.id) === String(dogId));
  let ownerId;
  if (ownsDog(current)) ownerId = currentId;
  else if (ownsDog(partner)) ownerId = partnerId;
  else if (!dogId && Number(current.user_dog_status) === 1 && Number(partner.user_dog_status) === 0) ownerId = currentId;
  else if (!dogId && Number(partner.user_dog_status) === 1 && Number(current.user_dog_status) === 0) ownerId = partnerId;
  if (!ownerId) return null;
  return { walker_id: ownerId === currentId ? partnerId : currentId, dog_owner_id: ownerId };
}

export function normalizeRecommendations(data) {
  if (data?.mode !== 'recommendations' || !Array.isArray(data.recommendations)) throw new Error('Invalid recommendation response');
  const seen = new Set();
  const venues = data.recommendations.flatMap((venue) => {
    const point = coordinates(venue);
    if (!point || !venue.location_id || !venue.name || seen.has(String(venue.location_id))) return [];
    seen.add(String(venue.location_id));
    return [{ ...venue, ...point, address: venue.address || venue.name }];
  }).slice(0, 5);
  return { venues, reason: data.reason };
}

export async function getMeetingParticipants(current, partnerId, dogId, signal) {
  if (!partnerId || String(partnerId) === String(current?.id)) throw new Error('Invalid participants');
  const result = await apiClient.get(`/user/details/${encodeURIComponent(partnerId)}`, { signal, timeout: 15000 });
  const partner = result.data?.status && result.data.data;
  const roles = partner && resolveRoles(current, partner, dogId);
  if (!roles) throw new Error('Cannot determine the dog owner for this booking');
  return { roles, partner };
}

export async function getRecommendations(roles, limits, signal, direct = false) {
  if (!recommendationPath) throw new Error('Recommendations not configured');
  const payload = { ...roles, meet_at_each_other: direct };
  if (!direct) {
    if (![limits.walker, limits.owner].every((n) => Number.isFinite(n) && n > 0)) throw new Error('Invalid walking limits');
    payload.user1_max_miles = limits.walker;
    payload.user2_max_miles = limits.owner;
  }
  const response = connection.direct
    ? await snapSoftClient.post(connection.url, payload, {
        signal, timeout: 20000,
        headers: { 'Content-Type': 'application/json', 'x-api-key': connection.apiKey },
      })
    : await apiClient.post(connection.url, payload, { signal, timeout: 20000 });
  return direct ? response.data : normalizeRecommendations(response.data);
}

export async function getNearbyMeetingPlaces(point, signal) {
  const response = await apiClient.post('/bookwalks/nearby-dogs-parks', { ...point, maxMiles: 50 }, { signal, timeout: 15000 });
  if (!response.data?.status) throw new Error('Could not load nearby places');
  const places = response.data.data || response.data.parks || [];
  if (!Array.isArray(places)) throw new Error('Invalid nearby places response');
  return places.flatMap((place) => {
    const point = coordinates(place);
    return point && place.name ? [{ ...place, ...point, address: place.address || place.name }] : [];
  });
}
